/* eslint-disable @angular-eslint/prefer-standalone */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DFormsReadService,
  EdpdRequestsDetailService,
  EdpdStorageService,
  IDynamicFormsDownloadedFile,
  IRequestAttachmentDetail,
  IRequestFormDetail,
} from '@eredes/dfcore';
import { DSM3ModalOptionsType, DSM3ModalService, DSM3NavItem, DSM3SelectOption } from '@eredes/dsm3';
import {
  IArchiveRecord,
  IAttachmentReplyResponse,
  ICommentReplyResponse,
  IGetAttachmentResponse,
  IRepliesResponse,
  MsCustomerService,
  MsStorageService,
} from '@eredes/microservices';
import * as FileSaver from 'file-saver';
import moment from 'moment';
import * as momentTimezone from 'moment-timezone';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { UserModel } from 'app/_core/models/user.model';
import { UserService } from 'app/_core/services/user.service';
import { NodeTabRequest } from 'app/request/interfaces/request.interface';
import {
  ATTENDING_POINT,
  COMM_CHANNEL_DIGITAL,
  DESTINY_E_REDES,
  INTERLOCUTOR_CLIENTE,
  INTERLOCUTOR_COMERCIALIZADOR,
  TYPE_SITE_COMERCIALIZADOR,
} from 'app/request-form/_constants/request-form.constants';
import { RequestFormHelper } from 'app/request-form/_helpers/request-form.helper';
import { EDownloadSourceType, IAttachment } from 'app/request-form/_models/attachment.interface';
import { DetailTitleViewModel } from 'app/request-form/_models/detail-title.view-model';
import { MessageItemViewModel } from 'app/request-form/_models/message-item.view-model';
import { ReopenRequestViewModel } from 'app/request-form/_models/reopen-request.view-model';
import {
  FormConfigurationFieldsCancelSubmit,
  FormConfigurationFieldsInsistenceSubmit,
  FormConfigurationFieldsReopenSubmit,
} from 'app/request-form/_models/request-form.interface';
import { RequestInformationViewModel } from 'app/request-form/_models/request-information.view-model';
import { RequestStateViewModel, StateStyle } from 'app/request-form/_models/request-state.view-model';
import { RequestStatus } from 'app/request-form/_models/request-status.enum';
import { ReplaceAllPipe } from 'app/request-form/_pipes/replace-all.pipe';
import { ClaimService } from 'app/request-form/_services/claim.service';
import { RequestFormService } from 'app/request-form/_services/request-form.service';

@Component({
  selector: 'app-request-jump-detail',
  templateUrl: './request-jump-detail.component.html',
  styleUrls: ['./request-jump-detail.component.scss'],
  providers: [ReplaceAllPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RequestJumpDetailComponent implements OnInit {
  @ViewChild('templateModalReopenRequest') templateModalReopenRequest: TemplateRef<unknown>;
  @ViewChild('templateModalCancelRequest') templateModalCancelRequest: TemplateRef<unknown>;
  formUuid: string;
  formData: IRequestFormDetail;
  detailTitleViewModel: DetailTitleViewModel;
  requestInformationViewModel: RequestInformationViewModel;
  requestStateViewModel: RequestStateViewModel;
  attachments: IAttachment[] = [];
  breadcrumbsItems$: Subject<DSM3NavItem[]> = new BehaviorSubject<DSM3NavItem[]>([]);
  user: UserModel;
  canSubmit: boolean;
  updateTransactionId: string;
  formCancelTransactionId: string;
  reopenTransactionId: string;

  reopenFormModal: any;
  reopenRequestViewModel: ReopenRequestViewModel;

  cancelFormModal: any;
  cancelRequestFormGroup: UntypedFormGroup;
  nodeTabRequest: NodeTabRequest;

  private readonly dFormsReadService = inject(DFormsReadService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly modalService = inject(DSM3ModalService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly userService = inject(UserService);
  private readonly requestFormService = inject(RequestFormService);
  private readonly router = inject(Router);
  private readonly msStorageService = inject(MsStorageService);
  private readonly msCustomerService = inject(MsCustomerService);
  private readonly replaceAllPipe = inject(ReplaceAllPipe);
  private readonly claimService = inject(ClaimService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly requestsDetailService = inject(EdpdRequestsDetailService);

  ngOnInit(): void {
    this.formUuid = this.route.snapshot.params.id;
    this.user = this.userService.getUser();

    this.configureRequestStateViewModel();
    this.getSubmittedForm();
    this.setupEventListeners();
  }

  get attachmentsForm(): UntypedFormArray {
    return this.requestStateViewModel.formGroup.get('attachments') as UntypedFormArray;
  }

  get attachmentsCancelRequestForm(): UntypedFormArray {
    return this.cancelRequestFormGroup.get('attachments') as UntypedFormArray;
  }

  /**
   * Method called when attachment is clicked to download
   * @param attachment attachment clicked from attachment list
   */
  downloadAttachment(attachment: IAttachment): void {
    if (attachment.source === EDownloadSourceType.MS_DYNAMIC_FORMS) {
      return this.downloadAttachmentDf(attachment);
    }

    if (attachment.source === EDownloadSourceType.MS_STORAGE) {
      return this.downloadMsCustomerGetAttachment(attachment);
    }

    return this.showErrorModal(
      'Erro',
      'Lamentamos mas de momento não é possível aceder ao seu ficheiro. Por favor tente mais tarde.'
    );
  }

  /**
   * Method to submit form data
   */
  submit(): void {
    this.configureUpdateTransactionId();
    const promiseFilesToUpload = this.configurePromiseFilesToUpload();
    const { state } = this.requestStateViewModel;

    Promise.all(promiseFilesToUpload).then((filesToUpload: IAttachment[]) => {
      const fields = this.mapperFormConfigurationFieldsInsistenceSubmit(filesToUpload);
      this.requestFormService.updateRequest(fields, state).subscribe({
        next: () => {
          this.showSuccessModalRedirectToList(
            'Resposta submetida com sucesso',
            'Pode consultar e acompanhar o seu pedido na área de pedidos.'
          );
        },
        error: () => {
          this.showErrorModal('Não foi possível submeter a sua resposta');
        },
      });
    });
  }

  /**
   * Method to perform some request update action
   */
  updateAction(): void {
    const CANCEL_STATUS: string[] = [RequestStatus.PENDING_RETAILER, RequestStatus.IN_PROGRESS];
    if (this.requestStateViewModel.state === RequestStatus.SOLVED) {
      const modal = this.modalService.confirm(this.viewContainerRef, {
        title: 'Reabertura de pedido',
        message: 'Tem certeza que pretende reabrir o pedido?',
      });

      modal.onSubmit().subscribe(() => {
        this.configureModalReopenRequest();
      });
    } else if (CANCEL_STATUS.includes(this.requestStateViewModel.state)) {
      this.configureModalCancelRequest();
    }
  }

  /**
   * Configure and show cancel request modal
   */
  configureModalCancelRequest(): void {
    this.cancelRequestFormGroup = this.createCancelForm();
    this.cancelFormModal = this.modalService.custom(this.templateModalCancelRequest, this.viewContainerRef);
  }

  /**
   * Configure and show reopen request modal
   */
  configureModalReopenRequest(): void {
    this.reopenRequestViewModel = new ReopenRequestViewModel(
      this.createReopenForm(),
      new BehaviorSubject<DSM3SelectOption[]>(this.getSelectableOptions())
    );

    this.reopenFormModal = this.modalService.custom(this.templateModalReopenRequest, this.viewContainerRef);
  }

  /**
   * Close modal that is used to reopen the order
   */
  closeReopenFormModal(): void {
    this.reopenFormModal.close();
  }

  /**
   * Close modal that is used to cancel the request
   */
  closeCancelFormModal(): void {
    this.cancelFormModal.close();
  }

  /**
   * Method called on clik cancel form button
   */
  submitCancelForm(): void {
    const { external_identification } = this.formData.form;

    const modal = this.modalService.confirm(this.viewContainerRef, {
      title: 'Anulação de pedido',
      message: `Tem a certeza que pretende anular o Pedido ${external_identification}?`,
    });

    modal.onSubmit().subscribe(() => {
      this.cancelForm();
    });
  }

  /**
   * Method to submit cancel request
   */
  cancelForm() {
    const promiseFilesToUpload = [];
    if (this.attachmentsCancelRequestForm) {
      this.attachmentsCancelRequestForm.controls.forEach((control) => {
        promiseFilesToUpload.push(RequestFormHelper.setReader(control.value));
      });
    }

    const fields: FormConfigurationFieldsCancelSubmit = this.mapperToSubmitCancelForm();
    Promise.all(promiseFilesToUpload).then((filesToUpload: IAttachment[]) => {
      this.requestFormService.cancel(this.formUuid, fields, filesToUpload).subscribe({
        next: () => {
          this.closeCancelFormModal();
          this.showSuccessModalRedirectToList('Sucesso', 'Pedido anulado com sucesso');
        },
        error: () => {
          this.showErrorModal('Erro', 'Não foi possível anular pedido');
        },
      });
    });
  }

  /**
   * Method to submit reopen request
   */
  submitReopenForm(): void {
    const data: FormConfigurationFieldsReopenSubmit = this.mapperToSubmitReopenForm();
    this.requestFormService.reopen(this.formUuid, data).subscribe({
      next: (response) => {
        this.closeReopenFormModal();
        this.showSuccessModalRedirectToList(
          'O seu pedido foi reaberto com sucesso',
          `Poderá fazer o acompanhamento no pedido com ID ${response.contact_id}.`
        );
      },
      error: () => {
        this.closeReopenFormModal();
        this.showErrorModal('Erro', 'Não foi possível criar o seu pedido');
      },
    });
  }

  /**
   * Return to request list page
   */
  back(): void {
    this.router.navigate(['request/solicitation']);
  }

  /**
   * Check that the form update is valid
   * @returns
   */
  checkValidForm(): boolean {
    const { attachments, description } = this.requestStateViewModel.formGroup.controls;

    return attachments.value.length > 0 || description.value;
  }

  /**
   * Filter selectable options depending on which form we're in
   * @returns filtered selectable options
   */
  private getSelectableOptions(): { value: string; label: string }[] {
    const selectableOptions = [
      { value: '01', label: 'Discordância com a solução/resposta' },
      { value: '02', label: 'Aditamento de prejuízos (se ação de prejuízos)' },
      { value: '03', label: 'Factos/dados novos' },
      { value: '04', label: 'Problema persiste ou não ficou resolvido' },
      { value: '05', label: 'Qualidade de serviço' },
      { value: '06', label: 'Funcionamento do equipamento de contagem' },
    ];
    const optionsToRemove = this.formData.form.step_uuid !== '609821e4-866c-4951-8242-fc8b92d00447' ? ['02'] : [];
    // Remove options as per https://agile.corp.edp.pt/browse/EDD-87205
    optionsToRemove.push('05', '06');

    if (optionsToRemove && optionsToRemove.length > 0) {
      for (const option of optionsToRemove) {
        selectableOptions.splice(
          selectableOptions.findIndex((selectable) => selectable.value === option),
          1
        );
      }
    }

    return selectableOptions;
  }

  /**
   * Initialize state and configure form update data
   */
  private configureRequestStateViewModel(): void {
    this.requestStateViewModel = {
      formGroup: this.createForm(),
      stateStyle: {
        bgState: '',
        colorState: '',
      },
      state: '',
      formNameDescription: 'description',
      errorsMessageDescription: { required: 'O campo é obrigatório' },
      formNameAttachment: 'attachments',
      canUpdateRequest: false,
      displayForm: true,
    };
  }

  /**
   * The function sets up event listeners for changes in the "attachments" form control and assigns a
   * unique transactionId to each attachment if it doesn't already have one.
   */
  private setupEventListeners(): void {
    this.requestStateViewModel.formGroup.controls['attachments'].valueChanges.subscribe({
      next: (value) => {
        this.requestStateViewModel.formGroup.controls['attachments'].setValue(
          value.map((item) => {
            if (!item.transactionId) {
              item.transactionId = crypto.randomUUID();
            }

            return item;
          }),
          { emitEvent: false }
        );
      },
    });
  }

  /**
   * Method responsible for creating the form
   */
  private createForm(): UntypedFormGroup {
    return this.fb.group({
      description: ['', [], []],
      attachments: new UntypedFormControl([]),
    });
  }

  /**
   * Search form details by formUuid
   */
  private getSubmittedForm(): void {
    this.requestsDetailService
      .getRequestDetail(this.formUuid, 'comercializadores')
      .subscribe((response: IRequestFormDetail) => {
        const { form } = response;
        this.claimService.findFormConfigByUuid(form.step_uuid).subscribe((nodeTabRequest: NodeTabRequest) => {
          this.nodeTabRequest = nodeTabRequest;
        });
        this.configureAllData(response, response.form.username);
      });
  }

  /**
   * Configure all necessary data for child components
   * @param response form detail
   * @param username username of the user who submitted the request
   */
  private configureAllData(response: IRequestFormDetail, username: string): void {
    this.formData = response;
    this.checkPermission(response).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
      this.setBreadcrumb(response);
      this.mapperDetailTitleViewModel(response, username);
      this.mapperRequestInformationViewModel(response);
      this.mapperRequestStateViewModel(response);
    });
  }

  /**
   * Check if logged user can submit form by uuid
   * @param response
   * @returns Observable true | false
   */
  private checkPermission(response: IRequestFormDetail): Observable<boolean> {
    return this.claimService.canSubmit(response.form.step_uuid);
  }

  /**
   * Initialize breadcrumb data
   * @param response form detail
   */
  private setBreadcrumb(response: IRequestFormDetail): void {
    const breadcrumbsItems: DSM3NavItem[] = [
      {
        label: 'Pedidos',
        href: '/request/solicitation',
      },
      {
        label: this.getIdWithFormType(response),
        active: true,
      },
    ];
    this.breadcrumbsItems$.next(breadcrumbsItems);
  }

  /**
   * Get title data from form detail
   * @param response form detail
   * @param username username of the user who submitted the request
   */
  private mapperDetailTitleViewModel(response: IRequestFormDetail, username: string): void {
    const { form } = response;

    const repeatOrderMessage = this.getChildRequestMessage(response);
    const actionText = RequestFormHelper.getNextActionByStatusName(
      form.status_label,
      () => !repeatOrderMessage && this.canSubmit
    );

    this.detailTitleViewModel = {
      title: form.step_title,
      id: this.getIdWithFormType(response),
      createBy: username,
      creationDate: moment(form.created_at).format('DD/MM/YYYY'),
      entityName: this.userService.isProfileRetailer() ? '' : form.entity_name,
      actionText,
    };
  }

  /**
   * Concat form identification with type
   * @param response form detail
   * @returns Ex: "PE - 0000000000"
   */
  private getIdWithFormType(response: IRequestFormDetail): string {
    const { form } = response;

    return `Pedido ${this.nodeTabRequest.type} - ${form.external_identification}`;
  }

  /**
   * Get main data from form detail
   * @param response form detail
   */
  private mapperRequestInformationViewModel(response: IRequestFormDetail): void {
    const { cpe, description, billingDate } = this.nodeTabRequest.formConfiguration.submit.fields;
    const { groups, attachments } = response;

    const cpeValue = RequestFormHelper.extractFieldByUuid(groups, cpe);
    const descriptionValue = RequestFormHelper.extractFieldByUuid(groups, description);
    const billingDateValue = RequestFormHelper.extractFieldByUuid(groups, billingDate);

    const date = moment(response.form.created_at).format('DD/MM/YYYY');

    const convertedAttachments = this.mapperAttachments(attachments.length > 0 ? attachments : [], date);

    this.attachments = [...this.attachments, ...convertedAttachments];

    const subType = this.nodeTabRequest.label;
    const type = this.nodeTabRequest.parent ? this.nodeTabRequest.parent.label : '';
    const area = this.nodeTabRequest.parent.parent ? this.nodeTabRequest.parent.parent.label : type;

    this.requestInformationViewModel = {
      area,
      type,
      subType,
      cpe: cpeValue,
      billingDate: billingDateValue,
      description: descriptionValue,
      attachments: convertedAttachments,
    };

    if (RequestFormHelper.hasParentForm(response)) {
      const parent = response.related.parent[0].parent[0];
      this.requestFormService
        .getRepliesByContactId(parent.external_identification_token)
        .subscribe((replyResponse: IRepliesResponse) => {
          const { comments } = replyResponse;
          const grupedComments = this.mapperCommentsToMessages(comments);

          if (grupedComments && grupedComments.length) {
            const comment = grupedComments[grupedComments.length - 1].message;

            this.requestInformationViewModel = {
              ...this.requestInformationViewModel,
              description: comment,
            };
          }

          this.cdr.detectChanges();
        });
    }
  }

  /**
   * Convert {@link IRequestAttachmentDetail} list to {@link IAttachment} list with {@link date}
   * @param formAttachments
   * @param date
   * @returns
   */
  private mapperAttachments(formAttachments: IRequestAttachmentDetail[], date: string): IAttachment[] {
    if (!formAttachments || !formAttachments.length) {
      return [];
    }

    formAttachments = formAttachments.filter((item: IRequestAttachmentDetail) => !!item.name);

    return formAttachments.map((item: IRequestAttachmentDetail) => {
      return {
        name: item.name,
        size: item.size,
        url: item.url,
        date,
        source: EDownloadSourceType.MS_DYNAMIC_FORMS,
        uuid: item.uuid,
      };
    });
  }

  /**
   * Checks if there is a new repeat order and returns a message with its identifier
   * @param response form detail
   * @returns
   */
  private getChildRequestMessage(response: IRequestFormDetail): MessageItemViewModel {
    const { related, form } = response;
    if (related && related.children && related.children.length > 0) {
      const firstChildren = related.children[0];

      if (firstChildren && firstChildren.children && firstChildren.children.length > 0) {
        const { external_identification, submitted_form_id, created_at } = firstChildren.children[0];
        const createdAt = momentTimezone.utc(created_at).tz('Europe/Lisbon').format('YYYY-MM-DD HH:mm:ss');

        if (form.status_label === RequestStatus.SOLVED) {
          return {
            author: 'Resposta automática',
            date: createdAt,
            message: `Foi criado um novo pedido com o ID <a href="/request/solicitation/detail/${submitted_form_id}">${external_identification}</a> para poder fazer o acompanhamento.`,
            attachments: [],
            haveLink: true,
            type: 'child',
            submitted_form_id: submitted_form_id,
            external_identification: external_identification,
          };
        }
      }
    }

    return null;
  }

  /**
   * Checks if there is a new repeat order and returns a message with its identifier
   * @param response form detail
   * @returns
   */
  private getParentRequestMessage(response: IRequestFormDetail): MessageItemViewModel {
    const { related } = response;
    const { created_at } = response.form;

    if (related.parent && related.parent.length > 0 && related.parent[0].parent.length > 0) {
      const { external_identification, submitted_form_id } = related.parent[0].parent[0];
      const createdAt = momentTimezone.utc(created_at).tz('Europe/Lisbon').format('YYYY-MM-DD HH:mm:ss');

      return {
        author: 'Resposta automática',
        date: createdAt,
        message: `Este pedido foi criado no seguimento da reabertura do pedido com o ID <a href="/request/solicitation/detail/${submitted_form_id}">${external_identification}</a> para poder fazer o acompanhamento.`,
        attachments: [],
        haveLink: true,
        type: 'parent',
        submitted_form_id: submitted_form_id,
        external_identification: external_identification,
      };
    }

    return null;
  }

  /**
   * Map comments returned by the service to list of messages to be displayed
   * @param comments
   * @returns
   */
  private mapperCommentsToMessages(comments: ICommentReplyResponse[]): MessageItemViewModel[] {
    const messages: MessageItemViewModel[] = [];

    if (comments && comments.length > 0) {
      comments.forEach((commentReply: ICommentReplyResponse) => {
        const { comment, last_modification, attachments, username, status } = commentReply;
        const commentDate = moment(last_modification).format('DD/MM/YYYY');
        const mappedAttachments: IAttachment[] = this.mapperAttachmentsReplyToAttachments(attachments, commentDate);

        messages.push({
          author: username,
          date: last_modification,
          message: this.replaceAllPipe.transform(comment, '->', ''),
          attachments: mappedAttachments,
          status,
          type: null,
        });
      });
    }

    return this.groupAndMergeMessages(messages);
  }

  /**
   * The function takes an array of message items, groups them by their date and time, and merges the
   * messages with the same date and time into a single message item.
   * @param  data - An array of MessageItemViewModel objects. Each object
   * represents a message and has properties such as author, date, message, and attachments.
   * @returns an array of MessageItemViewModel objects.
   */
  private groupAndMergeMessages(data: MessageItemViewModel[]): MessageItemViewModel[] {
    const groupedData = {};

    if (!data) {
      return [];
    }

    data.forEach((item) => {
      const dateTime = new Date(item.date); // Utiliza a data e hora completas como chave
      dateTime.setSeconds(0);

      const key = dateTime.toISOString() + (item.status ? '-' + item.status : '');

      if (!groupedData[key]) {
        groupedData[key] = {
          author: item.author,
          date: item.date,
          message: item.message,
          attachments: item.attachments,
          type: null,
        };
      } else {
        groupedData[key].message += item.message;

        if (item.attachments && item.attachments.length > 0) {
          groupedData[key].attachments = [...groupedData[key].attachments, ...item.attachments];
        }
      }
    });

    return Object.values(groupedData);
  }

  /**
   * Get attachments from comment list
   * @param comments
   * @returns
   */
  private mapperCommentsToAttachments(comments: ICommentReplyResponse[]): IAttachment[] {
    let mappedAttachments: IAttachment[] = [];
    comments.forEach((comment: ICommentReplyResponse) => {
      const { last_modification, attachments } = comment;
      const date = moment(last_modification).format('DD/MM/YYYY');

      if (attachments) {
        mappedAttachments = [...mappedAttachments, ...this.mapperAttachmentsReplyToAttachments(attachments, date)];
      }
    });

    return mappedAttachments;
  }

  /**
   * Map attachments returned by the service to list of attachment to be displayed
   * @param attachments - attachments from service
   * @param date - date to associate with attachments
   * @returns
   */
  private mapperAttachmentsReplyToAttachments(attachments: IAttachmentReplyResponse[], date: string): IAttachment[] {
    if (!attachments || !attachments.length) {
      return [];
    }

    return attachments.map((attachment: IAttachmentReplyResponse) => {
      const { doc_id, doc_extension, doc_name, size } = attachment;

      return {
        name: `${doc_name ? doc_name : doc_id + '.' + doc_extension}`,
        size,
        url: doc_id,
        date,
        source: EDownloadSourceType.MS_STORAGE,
      };
    });
  }

  /**
   * Get state from detail and configure form update data
   * @param response
   * @param username
   */
  private mapperRequestStateViewModel(response: IRequestFormDetail): void {
    const { form } = response;
    const stateStyle: StateStyle = RequestFormHelper.getStateStyleByStatusName(form.status_label);
    const canUpdateRequest = RequestFormHelper.canUpdateRequestByStatusName(form.status_label, () => this.canSubmit);
    const displayForm = RequestFormHelper.checkDisplayForm(form.status_label);

    const childRequestMessage: MessageItemViewModel = this.getChildRequestMessage(response);
    const parentRequestMessage: MessageItemViewModel = this.getParentRequestMessage(response);
    const messages = [];
    if (childRequestMessage) {
      messages.push(childRequestMessage);
    }
    if (parentRequestMessage) {
      messages.push(parentRequestMessage);
    }

    this.requestStateViewModel = {
      ...this.requestStateViewModel,
      state: form.status_label,
      stateStyle,
      messageListViewModel: {
        quantityStartingItems: 3,
        title: 'Mensagens',
        messages,
        emptyMessage: 'Não existem mensagens para este pedido',
      },
      canUpdateRequest,
      displayForm,
    };

    this.requestFormService.getRepliesByContactId(form.external_identification_token).subscribe({
      next: (replyResponse: IRepliesResponse) => {
        const { comments } = replyResponse;

        if (comments) {
          this.requestStateViewModel.messageListViewModel.messages = [
            ...this.requestStateViewModel.messageListViewModel.messages,
            ...this.mapperCommentsToMessages(comments),
          ];

          this.attachments = [...this.attachments, ...this.mapperCommentsToAttachments(comments)];
        }

        this.sortMessages();
        this.formatMessageDate();
        this.cdr.detectChanges();
      },
      error: () => {
        this.formatMessageDate();
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Sort list of {@link MessageItemViewModel} contained in object }{@link requestStateViewModel}
   */
  private sortMessages() {
    if (
      this.requestStateViewModel &&
      this.requestStateViewModel.messageListViewModel &&
      this.requestStateViewModel.messageListViewModel.messages
    ) {
      const { messages } = this.requestStateViewModel.messageListViewModel;
      messages.sort((a, b) => {
        if (moment(a.date).isAfter(moment(b.date))) {
          return -1;
        }

        return 1;
      });
    }
  }

  /**
   * Format message list dates contained in object {@link RequestStateViewModel}
   */
  private formatMessageDate() {
    if (
      this.requestStateViewModel &&
      this.requestStateViewModel.messageListViewModel &&
      this.requestStateViewModel.messageListViewModel.messages
    ) {
      const { messageListViewModel } = this.requestStateViewModel;
      const newMessages = messageListViewModel.messages.map((message) => {
        let date = '-';
        if (message.date) {
          const day = moment(message.date).format('DD/MM/YYYY');
          const hour = moment(message.date).format('HH:mm');
          date = `${day} às ${hour}`;
        }

        return { ...message, date };
      });

      this.requestStateViewModel = {
        ...this.requestStateViewModel,
        messageListViewModel: {
          ...messageListViewModel,
          messages: newMessages,
        },
      };
    }
  }

  /**
   * Method responsible for creating the reopen form
   */
  private createReopenForm(): UntypedFormGroup {
    return this.fb.group({
      reason: ['', [Validators.required], []],
      description: ['', [Validators.required], []],
    });
  }

  /**
   * Method responsible for creating the cancel form
   */
  private createCancelForm(): UntypedFormGroup {
    return this.fb.group({
      description: ['', [], []],
      attachments: this.fb.array([], [], []),
    });
  }

  /**
   * Set up object with the update data to reopen request
   * @returns {@link FormConfigurationFieldsSubmit}
   */
  private mapperToSubmitReopenForm(): FormConfigurationFieldsReopenSubmit {
    this.configureReopenTransactionId();
    const { reason, description } = this.reopenRequestViewModel.formGroup.controls;

    return {
      contactId: this.formData.form.external_identification_token,
      reason: [reason.value],
      description: description.value,
      transactionId: this.reopenTransactionId,
    };
  }

  /**
   * Set up object with the update data to cancel request
   * @returns {@link FormConfigurationFieldsSubmit}
   */
  private mapperToSubmitCancelForm(): FormConfigurationFieldsCancelSubmit {
    this.configureFormCancelTransactionId();
    const DEFAULT_DESCRIPTION = 'Pedido anulado pelo comercializador';
    const { attachments, description } = this.cancelRequestFormGroup.controls;

    return {
      contactId: this.formData.form.external_identification_token,
      partnerNumber: this.user.retailerPartnerNumber,
      attachments: attachments.value,
      description: description.value ? description.value : DEFAULT_DESCRIPTION,
      transactionId: this.formCancelTransactionId,
    };
  }

  /**
   * Method called when attachment from dynamic forms is clicked to download
   * @param attachment attachment clicked from attachment list
   */
  private downloadAttachmentDf(attachment: IAttachment): void {
    this.dFormsReadService
      .getFileDownload('s', attachment.uuid)
      .pipe(first())
      .subscribe({
        next: (fileStorage: IDynamicFormsDownloadedFile) => {
          if (!fileStorage) {
            return this.showErrorModal(
              'Erro',
              'Lamentamos mas de momento não é possível aceder ao seu ficheiro. Por favor tente mais tarde.'
            );
          }

          let mimeType = fileStorage.mimeType;
          let name = fileStorage.name;
          if (!fileStorage.mimeType) {
            mimeType = attachment.mime_type;
          }
          if (!fileStorage.name) {
            name = attachment.name;
          }
          const file = EdpdStorageService.base64toBlob(fileStorage.file, mimeType);
          FileSaver.saveAs(file, name);
        },
        error: () => {
          this.showErrorModal(
            'Erro',
            'Lamentamos mas de momento não é possível aceder ao seu ficheiro. Por favor tente mais tarde.'
          );
        },
      });
  }

  /**
   * Method called when attachment from microservice
   * customer is clicked to download
   * @param attachment - attachment clicked from attachment list
   */
  private downloadMsCustomerGetAttachment(attachment: IAttachment): void {
    const { external_identification_token } = this.formData.form;

    this.msCustomerService.getAttachments(external_identification_token, [attachment.url]).subscribe({
      next: (attachments: IGetAttachmentResponse[]) => {
        if (attachments && attachments.length > 0) {
          const { DocData, FileName } = attachments[0].CustomerContact.AttachDoc;
          const file = EdpdStorageService.base64toBlob(DocData, '');
          FileSaver.saveAs(file, FileName);
        } else {
          this.downloadMsStorageGetArchiveRecord(attachment);
        }
      },
      error: () => {
        this.downloadMsStorageGetArchiveRecord(attachment);
      },
    });
  }

  /**
   * Method called when attachment from microservice
   * storage is clicked to download
   * @param attachment - attachment clicked from attachment list
   */
  private downloadMsStorageGetArchiveRecord(attachment: IAttachment): void {
    this.msStorageService
      .getArchiveRecord(attachment.url)
      .pipe(first())
      .subscribe({
        next: (archiveRecord: IArchiveRecord) => {
          const file = EdpdStorageService.base64toBlob(archiveRecord.DocData, archiveRecord.MimeType);
          FileSaver.saveAs(file, attachment.name);
        },
        error: () => {
          this.showErrorModal(
            'Erro',
            'Lamentamos mas de momento não é possível aceder ao seu ficheiro. Por favor tente mais tarde.'
          );
        },
      });
  }

  /**
   * Generates a unique transaction ID if it does not already exist.
   */
  private configureUpdateTransactionId(): void {
    if (!this.updateTransactionId) {
      this.updateTransactionId = crypto.randomUUID();
    }
  }

  /**
   * Generates a unique transaction ID if it does not already exist.
   */
  private configureFormCancelTransactionId(): void {
    if (!this.formCancelTransactionId) {
      this.formCancelTransactionId = crypto.randomUUID();
    }
  }

  /**
   * Generates a unique transaction ID if it does not already exist.
   */
  private configureReopenTransactionId(): void {
    if (!this.reopenTransactionId) {
      this.reopenTransactionId = crypto.randomUUID();
    }
  }

  /**
   * Returns an array of promises that represent the files to be uploaded.
   * @returns an array of `IAttachment` objects.
   */
  private configurePromiseFilesToUpload(): IAttachment[] {
    const promiseFilesToUpload = [];
    if (this.attachmentsForm) {
      this.attachmentsForm.value.forEach((control) => {
        promiseFilesToUpload.push(RequestFormHelper.setReader(control));
      });
    }

    return promiseFilesToUpload;
  }

  /**
   * The function opens a success modal with a title and message, and then redirects to a specific page
   * when the modal is closed.
   * @param {string} title - A string representing the title of the success modal.
   * @param {string} message - The `message` parameter is a string that represents the message to be
   * displayed in the success modal.
   */
  private showSuccessModalRedirectToList(title: string, message: string): void {
    const modal = this.modalService.open(this.viewContainerRef, {
      title,
      message,
      type: DSM3ModalOptionsType.SUCCESS,
    });

    modal.onClose().subscribe(() => this.router.navigate(['request/solicitation']));
  }

  /**
   * The function showErrorModal opens a modal with an error message.
   * @param {string} title - A string representing the title of the error modal.
   * @param {string} [message] - The `message` parameter is an optional string that represents the
   * error message to be displayed in the modal.
   */
  private showErrorModal(title: string, message?: string): void {
    this.modalService.open(this.viewContainerRef, {
      title,
      message,
      type: DSM3ModalOptionsType.ERROR,
    });
  }

  /**
   * Returns the partner number value from the form data.
   * @returns a string value.
   */
  private getPartnerNumber(): string {
    const { clientPartnerNumber, partnerNumber } = this.nodeTabRequest.formConfiguration.submit.fields;
    const partnerNumberValue = RequestFormHelper.extractFieldByUuid(this.formData.groups, clientPartnerNumber);

    return partnerNumberValue
      ? partnerNumberValue
      : RequestFormHelper.extractFieldByUuid(this.formData.groups, partnerNumber);
  }

  /**
   * The function `addTransactionIdToFile` assigns a transaction ID to each file in the `filesToUpload`
   * array based on the corresponding transaction ID in the `attachmentsForm` array.
   * @param {IAttachment[]} filesToUpload - An array of objects representing the files to be uploaded.
   * Each object should have a property called "transactionId" which will be set in the function.
   * @returns void, which means it does not return any value.
   */
  private addTransactionIdToFile(filesToUpload: IAttachment[]): void {
    if (!filesToUpload || !filesToUpload.length) {
      return;
    }

    filesToUpload.forEach((file, index) => {
      file.transactionId = this.attachmentsForm.value[index].transactionId;
    });
  }

  /**
   * The function `mapperFormConfigurationFieldsInsistenceSubmit` maps form data and other variables to
   * create an object of type `FormConfigurationFieldsInsistenceSubmit`.
   * @param {IAttachment[]} filesToUpload - An array of attachments to be uploaded.
   * @returns an object of type `FormConfigurationFieldsInsistenceSubmit`.
   */
  private mapperFormConfigurationFieldsInsistenceSubmit(
    filesToUpload: IAttachment[]
  ): FormConfigurationFieldsInsistenceSubmit {
    const { clientPartnerNumberRequired } = this.nodeTabRequest.formConfiguration;
    const partnerNumberValue = this.getPartnerNumber();
    this.addTransactionIdToFile(filesToUpload);
    const currentDate = new Date();

    return {
      contactId: this.formData.form.external_identification_token,
      description: this.requestStateViewModel.formGroup.controls.description.value,
      attachments: filesToUpload,
      customerId: partnerNumberValue,
      type: TYPE_SITE_COMERCIALIZADOR,
      commChannel: COMM_CHANNEL_DIGITAL,
      destiny: DESTINY_E_REDES,
      interlocutor: clientPartnerNumberRequired ? INTERLOCUTOR_CLIENTE : INTERLOCUTOR_COMERCIALIZADOR,
      attendingPoint: ATTENDING_POINT,
      date: momentTimezone.utc(currentDate).tz('Europe/Lisbon').format('YYYY-MM-DD'),
      registryDate: momentTimezone.utc(currentDate).tz('Europe/Lisbon').format('YYYY-MM-DD'),
      registryTime: momentTimezone.utc(currentDate).tz('Europe/Lisbon').format('HH:mm:ss'),
      transactionId: this.updateTransactionId,
    };
  }
}
